<script setup>
import { ref, computed } from 'vue';

const props = defineProps({
    leadId: {
        type: Number,
        required: true,
    },
    leadName: {
        type: String,
        default: '',
    },
    leadEmail: {
        type: String,
        default: '',
    },
    leadCompany: {
        type: String,
        default: '',
    },
    domain: {
        type: [String, null],
        default: null,
    },
});

const emit = defineEmits(['close']);

const form = ref({
    product_name: '',
    description: '',
    value_to_customer: '',
    key_benefits: '',
    price: '',
    currency: 'SEK',
    timeline: '',
    additional_notes: '',
});

const isGenerating = ref(false);
const hasError = ref(false);
const budgetExceeded = ref(false);
const errorMessage = ref('');
const progressWidth = ref(0);
const progressInterval = ref(null);
const funMessageIndex = ref(0);
const funMessageInterval = ref(null);

const funMessages = [
    { icon: 'M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09z', text: () => _mft('crm:nina.quote.generating.analyzing') },
    { icon: 'M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25', text: () => _mft('crm:nina.quote.generating.researching') },
    { icon: 'M15.59 14.37a6 6 0 01-5.84 7.38v-4.8m5.84-2.58a14.98 14.98 0 006.16-12.12A14.98 14.98 0 009.631 8.41m5.96 5.96a14.926 14.926 0 01-5.841 2.58m-.119-8.54a6 6 0 00-7.381 5.84h4.8m2.581-5.84a14.927 14.927 0 00-2.58 5.84m2.699 2.7c-.103.021-.207.041-.311.06a15.09 15.09 0 01-2.448-2.448 14.9 14.9 0 01.06-.312m-2.24 2.39a4.493 4.493 0 00-1.757 4.306 4.493 4.493 0 004.306-1.758M16.5 9a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0z', text: () => _mft('crm:nina.quote.generating.drafting') },
    { icon: 'M9 12.75L11.25 15 15 9.75M21 12c0 1.268-.63 2.39-1.593 3.068a3.745 3.745 0 01-1.043 3.296 3.745 3.745 0 01-3.296 1.043A3.745 3.745 0 0112 21c-1.268 0-2.39-.63-3.068-1.593a3.746 3.746 0 01-3.296-1.043 3.745 3.745 0 01-1.043-3.296A3.745 3.745 0 013 12c0-1.268.63-2.39 1.593-3.068a3.745 3.745 0 011.043-3.296 3.746 3.746 0 013.296-1.043A3.746 3.746 0 0112 3c1.268 0 2.39.63 3.068 1.593a3.746 3.746 0 013.296 1.043 3.745 3.745 0 011.043 3.296A3.745 3.745 0 0121 12z', text: () => _mft('crm:nina.quote.generating.finishing') },
];

const currentFunMessage = computed(() => funMessages[funMessageIndex.value % funMessages.length]);

const canSubmit = computed(() => {
    return form.value.product_name.trim() !== '' && form.value.value_to_customer.trim() !== '';
});

const startGenerating = () => {
    isGenerating.value = true;
    funMessageIndex.value = Math.floor(Math.random() * funMessages.length);
    progressWidth.value = 0;

    funMessageInterval.value = setInterval(() => {
        funMessageIndex.value = (funMessageIndex.value + 1) % funMessages.length;
    }, 3500);

    let elapsed = 0;
    progressInterval.value = setInterval(() => {
        elapsed += 200;
        progressWidth.value = Math.min(90, 30 * Math.log10(elapsed / 500 + 1));
    }, 200);
};

const stopGenerating = () => {
    isGenerating.value = false;
    if (funMessageInterval.value) {
        clearInterval(funMessageInterval.value);
        funMessageInterval.value = null;
    }
    if (progressInterval.value) {
        clearInterval(progressInterval.value);
        progressInterval.value = null;
    }
    progressWidth.value = 100;
};

const showBudgetExceeded = () => {
    hasError.value = true;
    budgetExceeded.value = true;
    errorMessage.value = _mft('crm:nina.error.budgetExceeded');
};

const submitForm = async () => {
    if (!canSubmit.value || isGenerating.value) return;

    hasError.value = false;
    budgetExceeded.value = false;
    errorMessage.value = '';
    startGenerating();

    try {
        const res = await axios.post(route('nina.quote.generate'), {
            lead_id: props.leadId,
            domain: props.domain,
            ...form.value,
        });

        if (res.data.status === 'budget_exceeded') {
            stopGenerating();
            showBudgetExceeded();
            return;
        }

        const contractId = res.data.contract_id;

        const poll = async () => {
            try {
                const pollRes = await axios.get(route('nina.quote.poll', contractId));
                if (pollRes.data.status === 'completed') {
                    stopGenerating();
                    window.location.href = route('expert-contracts.edit', contractId);
                    return;
                }
                if (pollRes.data.status === 'budget_exceeded') {
                    stopGenerating();
                    showBudgetExceeded();
                    return;
                }
                if (pollRes.data.status === 'failed') {
                    stopGenerating();
                    hasError.value = true;
                    errorMessage.value = _mft('crm:nina.quote.error.generationFailed');
                    return;
                }
                setTimeout(poll, 2000);
            } catch (e) {
                stopGenerating();
                hasError.value = true;
                errorMessage.value = _mft('crm:nina.quote.error.generationFailed');
            }
        };

        setTimeout(poll, 2000);
    } catch (e) {
        stopGenerating();
        hasError.value = true;
        if (e.response?.status === 403) {
            errorMessage.value = _mft('crm:nina.error.notHired');
        } else {
            errorMessage.value = _mft('crm:nina.quote.error.generationFailed');
        }
    }
};
</script>

<template>
    <slideout-with-slots
        max-width-class="max-w-2xl"
        :resizeable="true"
        @close="emit('close')"
    >
        <template #title>
            <div>
                <h1 class="text-lg font-semibold">
                    <mf-translate domainkey="crm:nina.quote.title"></mf-translate>
                </h1>
                <p v-if="leadName || leadCompany" class="text-sm opacity-80">
                    {{ leadName }}<span v-if="leadName && leadCompany"> &middot; </span>{{ leadCompany }}
                </p>
            </div>
        </template>

        <template #body>
            <!-- Generating state -->
            <div v-if="isGenerating" class="flex flex-col items-center justify-center py-16">
                <div class="w-full max-w-md rounded-2xl overflow-hidden shadow-lg border border-gray-100 bg-gradient-to-br from-gray-50 via-white to-gray-50">
                    <div class="h-1 w-full bg-gray-200 overflow-hidden">
                        <div
                            class="h-full bg-aiAgentSalesAssistant transition-all duration-700 ease-out"
                            :style="{ width: progressWidth + '%' }"
                        ></div>
                    </div>
                    <div class="px-5 py-6">
                        <div class="flex items-center gap-3 mb-4">
                            <div class="flex-shrink-0 w-10 h-10 rounded-lg bg-aiAgentSalesAssistant flex items-center justify-center">
                                <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-gray-800" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
                                    <path stroke-linecap="round" stroke-linejoin="round" :d="currentFunMessage.icon" />
                                </svg>
                            </div>
                            <span class="text-sm font-medium text-gray-700">{{ currentFunMessage.text() }}</span>
                        </div>
                        <div class="space-y-2">
                            <div class="flex gap-2">
                                <div class="h-2 rounded-full bg-gray-200/60 animate-pulse" style="width: 65%"></div>
                                <div class="h-2 rounded-full bg-gray-200/60 animate-pulse" style="width: 25%"></div>
                            </div>
                            <div class="flex gap-2">
                                <div class="h-2 rounded-full bg-gray-200/60 animate-pulse" style="width: 40%"></div>
                                <div class="h-2 rounded-full bg-gray-200/60 animate-pulse" style="width: 50%"></div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Form -->
            <div v-else class="space-y-6">
                <p class="text-sm text-gray-500">
                    <mf-translate domainkey="crm:nina.quote.description"></mf-translate>
                </p>

                <div>
                    <label class="block text-sm font-medium text-gray-700 mb-1">
                        <mf-translate domainkey="crm:nina.quote.field.productName"></mf-translate> *
                    </label>
                    <input
                        v-model="form.product_name"
                        type="text"
                        class="w-full px-4 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-aiAgentSalesAssistant focus:border-aiAgentSalesAssistant"
                        :placeholder="_mft('crm:nina.quote.placeholder.productName')"
                    />
                </div>

                <div>
                    <label class="block text-sm font-medium text-gray-700 mb-1">
                        <mf-translate domainkey="crm:nina.quote.field.description"></mf-translate>
                    </label>
                    <textarea
                        v-model="form.description"
                        rows="3"
                        maxlength="2000"
                        class="w-full px-4 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-aiAgentSalesAssistant focus:border-aiAgentSalesAssistant"
                        :placeholder="_mft('crm:nina.quote.placeholder.description')"
                    ></textarea>
                    <div class="flex justify-end mt-1">
                        <span class="text-xs" :class="form.description.length > 1800 ? 'text-amber-500' : 'text-gray-400'">{{ form.description.length }} / 2000</span>
                    </div>
                </div>

                <div>
                    <label class="block text-sm font-medium text-gray-700 mb-1">
                        <mf-translate domainkey="crm:nina.quote.field.valueToCustomer"></mf-translate> *
                    </label>
                    <textarea
                        v-model="form.value_to_customer"
                        rows="3"
                        maxlength="2000"
                        class="w-full px-4 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-aiAgentSalesAssistant focus:border-aiAgentSalesAssistant"
                        :placeholder="_mft('crm:nina.quote.placeholder.valueToCustomer')"
                    ></textarea>
                    <div class="flex justify-end mt-1">
                        <span class="text-xs" :class="form.value_to_customer.length > 1800 ? 'text-amber-500' : 'text-gray-400'">{{ form.value_to_customer.length }} / 2000</span>
                    </div>
                </div>

                <div>
                    <label class="block text-sm font-medium text-gray-700 mb-1">
                        <mf-translate domainkey="crm:nina.quote.field.keyBenefits"></mf-translate>
                    </label>
                    <textarea
                        v-model="form.key_benefits"
                        rows="2"
                        maxlength="2000"
                        class="w-full px-4 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-aiAgentSalesAssistant focus:border-aiAgentSalesAssistant"
                        :placeholder="_mft('crm:nina.quote.placeholder.keyBenefits')"
                    ></textarea>
                    <div class="flex justify-end mt-1">
                        <span class="text-xs" :class="form.key_benefits.length > 1800 ? 'text-amber-500' : 'text-gray-400'">{{ form.key_benefits.length }} / 2000</span>
                    </div>
                </div>

                <div class="grid grid-cols-3 gap-4">
                    <div class="col-span-2">
                        <label class="block text-sm font-medium text-gray-700 mb-1">
                            <mf-translate domainkey="crm:nina.quote.field.price"></mf-translate>
                        </label>
                        <input
                            v-model="form.price"
                            type="text"
                            class="w-full px-4 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-aiAgentSalesAssistant focus:border-aiAgentSalesAssistant"
                            :placeholder="_mft('crm:nina.quote.placeholder.price')"
                        />
                    </div>
                    <div>
                        <label class="block text-sm font-medium text-gray-700 mb-1">
                            <mf-translate domainkey="crm:nina.quote.field.currency"></mf-translate>
                        </label>
                        <select
                            v-model="form.currency"
                            class="w-full px-4 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-aiAgentSalesAssistant focus:border-aiAgentSalesAssistant"
                        >
                            <option value="SEK">SEK</option>
                            <option value="NOK">NOK</option>
                            <option value="EUR">EUR</option>
                            <option value="USD">USD</option>
                        </select>
                    </div>
                </div>

                <div>
                    <label class="block text-sm font-medium text-gray-700 mb-1">
                        <mf-translate domainkey="crm:nina.quote.field.timeline"></mf-translate>
                    </label>
                    <input
                        v-model="form.timeline"
                        type="text"
                        class="w-full px-4 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-aiAgentSalesAssistant focus:border-aiAgentSalesAssistant"
                        :placeholder="_mft('crm:nina.quote.placeholder.timeline')"
                    />
                </div>

                <div>
                    <label class="block text-sm font-medium text-gray-700 mb-1">
                        <mf-translate domainkey="crm:nina.quote.field.additionalNotes"></mf-translate>
                    </label>
                    <textarea
                        v-model="form.additional_notes"
                        rows="2"
                        maxlength="2000"
                        class="w-full px-4 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-aiAgentSalesAssistant focus:border-aiAgentSalesAssistant"
                        :placeholder="_mft('crm:nina.quote.placeholder.additionalNotes')"
                    ></textarea>
                    <div class="flex justify-end mt-1">
                        <span class="text-xs" :class="form.additional_notes.length > 1800 ? 'text-amber-500' : 'text-gray-400'">{{ form.additional_notes.length }} / 2000</span>
                    </div>
                </div>

                <div v-if="hasError" class="px-4 py-2 text-sm text-red-600 bg-red-50 rounded-lg">
                    {{ errorMessage }}
                    <utility-ai-usage-cta v-if="budgetExceeded" class="mt-2" />
                </div>

                <button-base
                    @click="submitForm"
                    style-type="primary"
                    :disabled="!canSubmit"
                    class="w-full"
                >
                    <template #icon>
                        <i class="fa-regular fa-file-signature mr-1.5"></i>
                    </template>
                    <mf-translate domainkey="crm:nina.quote.draftQuote"></mf-translate>
                </button-base>
            </div>
        </template>
    </slideout-with-slots>
</template>
